/* eslint react-hooks/exhaustive-deps: "warn" */

import { useMemo, useState } from 'react';

import { useInt } from 'hooks/useInt';

import { CompanyDataExplorer } from 'features/company-data/organisms/CompanyDataExplorer/CompanyDataExplorer';
import type {
  ExplorerColumn,
  ExplorerItem,
} from 'features/company-data/organisms/CompanyDataExplorer/CompanyDataExplorer.types';

type Translate = (key: string) => string;

type Industry = {
  key: string;
  name: string;
  iconType: string;
  companies: number;
  contacts: number;
  accuracy: number;
};

const getIndustries = (t: Translate): Industry[] => [
  {
    key: 'agriculture',
    name: t('Agriculture'),
    iconType: 'industry-1',
    companies: 8420318,
    contacts: 14820640,
    accuracy: 91.2,
  },
  {
    key: 'artificial-intelligence',
    name: t('Artificial Intelligence and Machine Learning'),
    iconType: 'industry-2',
    companies: 412820,
    contacts: 1820402,
    accuracy: 96.4,
  },
  {
    key: 'automotive',
    name: t('Automotive'),
    iconType: 'industry-3',
    companies: 2810402,
    contacts: 6420820,
    accuracy: 93.8,
  },
  {
    key: 'beverages',
    name: t('Beverages'),
    iconType: 'industry-4',
    companies: 642108,
    contacts: 1420840,
    accuracy: 92.1,
  },
  {
    key: 'big-data',
    name: t('Big Data'),
    iconType: 'industry-5',
    companies: 286402,
    contacts: 1108220,
    accuracy: 95.7,
  },
  {
    key: 'business-services',
    name: t('Business services'),
    iconType: 'industry-6',
    companies: 18420820,
    contacts: 38420108,
    accuracy: 94.2,
  },
  {
    key: 'chemicals',
    name: t('Chemicals'),
    iconType: 'industry-7',
    companies: 1820402,
    contacts: 4108620,
    accuracy: 92.5,
  },
  {
    key: 'construction',
    name: t('Construction and Real Estate'),
    iconType: 'industry-8',
    companies: 24820108,
    contacts: 48620402,
    accuracy: 93.1,
  },
  {
    key: 'consulting',
    name: t('Consulting'),
    iconType: 'industry-9',
    companies: 6420820,
    contacts: 14820108,
    accuracy: 94.6,
  },
  {
    key: 'consumer-goods',
    name: t('Consumer Goods and Services'),
    iconType: 'industry-10',
    companies: 12420108,
    contacts: 28620820,
    accuracy: 93.4,
  },
  {
    key: 'cybersecurity',
    name: t('Cybersecurity'),
    iconType: 'industry-11',
    companies: 348620,
    contacts: 1420108,
    accuracy: 96.2,
  },
  {
    key: 'defence',
    name: t('Defence and Security'),
    iconType: 'industry-12',
    companies: 286820,
    contacts: 720402,
    accuracy: 95.5,
  },
  {
    key: 'ecommerce',
    name: t('E-Commerce'),
    iconType: 'industry-13',
    companies: 8420620,
    contacts: 18420108,
    accuracy: 94.9,
  },
  {
    key: 'education',
    name: t('Education and Training'),
    iconType: 'industry-14',
    companies: 4820108,
    contacts: 10820402,
    accuracy: 93.7,
  },
  {
    key: 'energy',
    name: t('Energy and Utilities'),
    iconType: 'industry-15',
    companies: 1620408,
    contacts: 4108620,
    accuracy: 93.2,
  },
  {
    key: 'finance',
    name: t('Finance and Insurance'),
    iconType: 'industry-16',
    companies: 4820108,
    contacts: 12420402,
    accuracy: 95.8,
  },
  {
    key: 'fintech',
    name: t('Fintech'),
    iconType: 'industry-17',
    companies: 482620,
    contacts: 1620402,
    accuracy: 96.1,
  },
  { key: 'food', name: t('Food'), iconType: 'industry-18', companies: 6820402, contacts: 14820108, accuracy: 91.9 },
  {
    key: 'government',
    name: t('Government'),
    iconType: 'industry-19',
    companies: 218620,
    contacts: 620402,
    accuracy: 94.8,
  },
  {
    key: 'hire-rental',
    name: t('Hire and Rental'),
    iconType: 'industry-20',
    companies: 820402,
    contacts: 1820108,
    accuracy: 91.5,
  },
  {
    key: 'human-resources',
    name: t('Human Resources'),
    iconType: 'industry-21',
    companies: 2820108,
    contacts: 6420402,
    accuracy: 93.6,
  },
  {
    key: 'it',
    name: t('Information Technology'),
    iconType: 'industry-22',
    companies: 6820108,
    contacts: 18420402,
    accuracy: 95.4,
  },
  { key: 'legal', name: t('Legal'), iconType: 'industry-23', companies: 1420820, contacts: 3820108, accuracy: 95.1 },
  {
    key: 'leisure',
    name: t('Leisure and Entertainment'),
    iconType: 'industry-24',
    companies: 3420108,
    contacts: 7820402,
    accuracy: 92.7,
  },
  {
    key: 'manufacturing',
    name: t('Manufacturing'),
    iconType: 'industry-25',
    companies: 18420108,
    contacts: 42820620,
    accuracy: 93.3,
  },
  {
    key: 'marketing',
    name: t('Marketing, Sales & PR'),
    iconType: 'industry-26',
    companies: 4820820,
    contacts: 12420108,
    accuracy: 94.3,
  },
  { key: 'media', name: t('Media'), iconType: 'industry-27', companies: 1820108, contacts: 4820402, accuracy: 93.5 },
  { key: 'metals', name: t('Metals'), iconType: 'industry-28', companies: 1420108, contacts: 3420820, accuracy: 92.3 },
  {
    key: 'mining',
    name: t('Mining and Minerals'),
    iconType: 'industry-29',
    companies: 820108,
    contacts: 1820402,
    accuracy: 91.7,
  },
  {
    key: 'nonprofit',
    name: t('Nonprofit Organisations'),
    iconType: 'industry-30',
    companies: 2820820,
    contacts: 6420108,
    accuracy: 92.9,
  },
  {
    key: 'oil-gas',
    name: t('Oil and Gas'),
    iconType: 'industry-31',
    companies: 420820,
    contacts: 1108402,
    accuracy: 94.4,
  },
  {
    key: 'paper-wood',
    name: t('Paper, Wood and Furniture'),
    iconType: 'industry-32',
    companies: 1420402,
    contacts: 3420108,
    accuracy: 92.6,
  },
  {
    key: 'pharmaceutical',
    name: t('Pharmaceutical and Medical'),
    iconType: 'industry-33',
    companies: 1820620,
    contacts: 4820108,
    accuracy: 95.6,
  },
  {
    key: 'retail',
    name: t('Retail'),
    iconType: 'industry-34',
    companies: 14820108,
    contacts: 34820620,
    accuracy: 94.0,
  },
  {
    key: 'telecommunication',
    name: t('Telecommunication'),
    iconType: 'industry-35',
    companies: 642820,
    contacts: 1820402,
    accuracy: 95.3,
  },
  {
    key: 'textile',
    name: t('Textile'),
    iconType: 'industry-36',
    companies: 1820108,
    contacts: 4108620,
    accuracy: 91.4,
  },
  {
    key: 'tourism',
    name: t('Tourism'),
    iconType: 'industry-37',
    companies: 4820402,
    contacts: 10820108,
    accuracy: 92.8,
  },
  {
    key: 'transport',
    name: t('Transport and Logistics'),
    iconType: 'industry-38',
    companies: 6820820,
    contacts: 14820402,
    accuracy: 93.9,
  },
  {
    key: 'wholesale',
    name: t('Wholesale'),
    iconType: 'industry-39',
    companies: 8420402,
    contacts: 18420108,
    accuracy: 93.0,
  },
];

const COLUMN_COUNT = 3;

const GLOBAL_METRICS = {
  companies: 750812956,
  contacts: 1483956356,
  accuracy: 94.7,
};

const toExplorerItem = (industry: Industry): ExplorerItem => ({
  key: industry.key,
  name: industry.name,
  visual: { type: 'icon', iconType: industry.iconType },
  metrics: {
    companies: industry.companies,
    contacts: industry.contacts,
    accuracy: industry.accuracy,
  },
});

export const CompanyDataByIndustry = () => {
  const { t } = useInt();
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');

  const industries = useMemo(() => getIndustries(t), [t]);
  const allItems = useMemo(() => industries.map(toExplorerItem), [industries]);

  const columns = useMemo<ExplorerColumn[]>(() => {
    const sorted = [...industries].sort((a, b) => a.name.localeCompare(b.name));
    const perCol = Math.ceil(sorted.length / COLUMN_COUNT);

    return Array.from({ length: COLUMN_COUNT }, (_, index) => ({
      key: `col-${index}`,
      items: sorted.slice(index * perCol, (index + 1) * perCol).map(toExplorerItem),
    }));
  }, [industries]);

  return (
    <CompanyDataExplorer
      title={t('Company data by industry')}
      description={t('Explore company and contact coverage across industries')}
      allItems={allItems}
      columns={columns}
      selectedKey={selectedIndustry}
      onSelectKey={setSelectedIndustry}
      globalMetrics={GLOBAL_METRICS}
      tooltipFallbackTitle={t('Data Quality Metrics')}
      tooltipMeta={t('Latest update: Today, 09:15')}
    />
  );
};
